import { sendGtmEvent } from "../analytics/definitions";

const storageKey = "activeFeatureVariations";
const gtmEventName = "updateVariationId";

export type AbFeatureVariation = {
  featureName: string;
  featureOption: string;
};

/**
 * Updates the session storage with new feature flag variations and sends a GTM event.
 *
 * This function compares the provided A/B test response options with the existing
 * feature variations stored in the session. It identifies new variations, updates
 * the session storage, and triggers a GTM event to log the updated variations.
 *
 * @param {AbFeatureVariation[]} abResponseOptions - The list of feature variations
 * received from the A/B test response.
 *
 * @returns {Promise<void>} Resolves when the session storage and GTM event are updated.
 */
const updateVariationIds = async (
  abResponseOptions: AbFeatureVariation[]
): Promise<void> => {
  const existingFeatureVariations =
    sessionStorage.getItem(storageKey)?.split(",") || [];

  const newFeatureVariations = filterNewFeatureVariations(
    abResponseOptions,
    existingFeatureVariations
  );

  if (!newFeatureVariations.length) {
    return;
  }

  const updatedFeatureVariations = [
    ...existingFeatureVariations.filter(Boolean),
    ...newFeatureVariations,
  ].join(",");

  sessionStorage.setItem(storageKey, updatedFeatureVariations);

  sendGtmEvent({
    event: gtmEventName,
    updatedVariationId: updatedFeatureVariations,
  });
};

/**
 * Filters and formats new feature variations that are not already stored in session storage.
 *
 * @param {AbFeatureVariation[]} abResponseOptions - The list of feature variations
 * received from the A/B test response.
 * @param {string[]} existingFeatureVariations - The list of existing feature variations
 * stored in session storage.
 *
 * @returns {string[]} A list of new feature variations in the "featureName:featureOption" format.
 */
const filterNewFeatureVariations = (
  abResponseOptions: AbFeatureVariation[],
  existingFeatureVariations: string[]
): string[] => {
  const existingFeatureNames = new Set(
    existingFeatureVariations.map((variation) =>
      variation.slice(0, variation.indexOf(":"))
    )
  );

  return abResponseOptions
    .filter((option) => !existingFeatureNames.has(option.featureName))
    .map((option) => `${option.featureName}:${option.featureOption}`);
};

export default updateVariationIds;
